'use client';
import { useEffect, useRef } from 'react';
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
/**
 * Focus management for a modal/dialog/lightbox, so hand-rolled overlays meet
 * WCAG 2.4.3 (focus order) and 2.1.2 (no keyboard trap *out* of the dialog):
 *
 *  - moves focus into the container when it opens,
 *  - keeps Tab/Shift+Tab cycling within it,
 *  - calls `onEscape` on Esc,
 *  - restores focus to the previously-focused element when it closes.
 *
 * Single source of truth; re-exported by `@gjl/ui` and `@gjl/site-kit` so both
 * the component apps and the bespoke site forks import the same implementation.
 * Attach the returned ref to the dialog container and gate it on the open state:
 *
 *   const ref = useFocusTrap<HTMLDivElement>(open, { onEscape: close })
 *   return open ? <div ref={ref} role="dialog" aria-modal="true">…</div> : null
 *
 * Call it before any early return (rules-of-hooks). Prefer Radix `Dialog` for
 * new modals (it does this for free); this hook retrofits existing bespoke
 * overlays without restructuring their markup.
 */
export function useFocusTrap(active, opts = {}) {
    const ref = useRef(null);
    const onEscapeRef = useRef(opts.onEscape);
    onEscapeRef.current = opts.onEscape;
    useEffect(() => {
        if (!active)
            return;
        const container = ref.current;
        if (!container)
            return;
        const previouslyFocused = document.activeElement;
        const focusables = () => Array.from(container.querySelectorAll(FOCUSABLE)).filter((el) => el.offsetParent !== null || el === document.activeElement);
        const first = focusables()[0];
        if (first) {
            first.focus();
        }
        else {
            container.setAttribute('tabindex', '-1');
            container.focus();
        }
        const onKeyDown = (e) => {
            if (e.key === 'Escape') {
                onEscapeRef.current?.();
                return;
            }
            if (e.key !== 'Tab')
                return;
            const items = focusables();
            if (items.length === 0) {
                e.preventDefault();
                return;
            }
            const firstEl = items[0];
            const lastEl = items[items.length - 1];
            const activeEl = document.activeElement;
            if (e.shiftKey) {
                if (activeEl === firstEl || !container.contains(activeEl)) {
                    e.preventDefault();
                    lastEl.focus();
                }
            }
            else if (activeEl === lastEl || !container.contains(activeEl)) {
                e.preventDefault();
                firstEl.focus();
            }
        };
        document.addEventListener('keydown', onKeyDown, true);
        return () => {
            document.removeEventListener('keydown', onKeyDown, true);
            previouslyFocused?.focus?.();
        };
    }, [active]);
    return ref;
}
